import { apiService } from './api';

interface RegisterVendorData {
  vendorType: 'UPI' | 'BANK_ACCOUNT';
  label?: string;
  name: string;
  email: string;
  phone: string;
  upiId?: string;
  accountNumber?: string;
  ifsc?: string;
  scheduleOption?: number;
  verifyAccount?: boolean;
  kycDetails?: {
    pan?: string;
    gst?: string;
    cin?: string;
    accountType?: string;
    businessType?: string;
  };
  [key: string]: any;
}

interface UpdateVendorData {
  label?: string;
  name?: string;
  email?: string;
  phone?: string;
  scheduleOption?: number;
  kycDetails?: {
    pan?: string;
    gst?: string;
    cin?: string;
    accountType?: string;
    businessType?: string;
  };
  [key: string]: any;
}

type PaymentGatewayType = 'cashfree' | 'manual';

/**
 * Unified Payment Gateway Service
 * Supports multiple payment gateway types through a unified API
 */
class PaymentGatewayService {
  private type: PaymentGatewayType;

  constructor(type: PaymentGatewayType = 'cashfree') {
    this.type = type;
  }

  /**
   * Get base URL for gateway type
   */
  private getBaseUrl(): string {
    return `/payment-gateway/${this.type}`;
  }

  /**
   * Register a new vendor account (UPI or Bank)
   */
  async registerVendor(data: RegisterVendorData) {
    try {
      const response = await apiService.post(`${this.getBaseUrl()}/vendors`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error registering vendor:', error);
      throw error;
    }
  }

  /**
   * Get all vendors for the current company
   */
  async getVendors(filters?: any) {
    try {
      const response = await apiService.get(`${this.getBaseUrl()}/vendors`, { params: filters });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching vendors:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch vendors',
        data: []
      };
    }
  }

  /**
   * Get specific vendor by ID
   */
  async getVendor(vendorId: string) {
    const response = await apiService.get(`/payment-gateway/${this.type}/vendors/${vendorId}`);
    return response.data;
  }

  /**
   * Get properties assigned to a specific vendor
   */
  async getVendorProperties(vendorId: string) {
    const response = await apiService.get(`/payment-gateway/${this.type}/vendors/${vendorId}/properties`);
    return response.data;
  }

  /**
   * Assign vendor config to a property
   */
  async assignVendorToProperty(vendorId: string, propertyId: string) {
    const response = await apiService.post(`/payment-gateway/${this.type}/vendors/${vendorId}/assign-property`, {
      propertyId
    });
    return response.data;
  }

  /**
   * Update vendor information
   */
  async updateVendor(vendorId: string, data: UpdateVendorData) {
    try {
      const response = await apiService.put(`${this.getBaseUrl()}/vendors/${vendorId}`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating vendor:', error);
      throw error;
    }
  }

  /**
   * Delete vendor (soft delete)
   */
  async deleteVendor(vendorId: string) {
    try {
      const response = await apiService.delete(`${this.getBaseUrl()}/vendors/${vendorId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting vendor:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete vendor'
      };
    }
  }

  /**
   * Set vendor as default for the company
   */
  async setDefaultVendor(vendorId: string) {
    try {
      const response = await apiService.post(`${this.getBaseUrl()}/vendors/${vendorId}/set-default`);
      return response.data;
    } catch (error: any) {
      console.error('Error setting default vendor:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to set default vendor'
      };
    }
  }

  /**
   * Toggle payment gateway active/inactive status
   */
  async togglePaymentGateway(isActive: boolean) {
    try {
      const response = await apiService.post(`${this.getBaseUrl()}/toggle`, {
        isActive
      });
      return response.data;
    } catch (error: any) {
      console.error('Error toggling payment gateway:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to toggle payment gateway'
      };
    }
  }

  /**
   * Get payment gateway status and configuration
   */
  async getPaymentGatewayStatus() {
    try {
      const response = await apiService.get(`${this.getBaseUrl()}/status`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching payment gateway status:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch payment gateway status',
        data: {
          type: this.type,
          isConfigured: false,
          isActive: false,
          defaultVendor: null
        }
      };
    }
  }

  /**
   * Sync vendor status from payment gateway (Cashfree only)
   */
  async syncVendor(vendorId: string) {
    try {
      const response = await apiService.post(`${this.getBaseUrl()}/vendors/${vendorId}/sync`);
      return response.data;
    } catch (error: any) {
      console.error('Error syncing vendor:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to sync vendor status'
      };
    }
  }

  /**
   * Get settlement history (Cashfree only)
   */
  async getSettlements(filters?: any) {
    try {
      const response = await apiService.get(`${this.getBaseUrl()}/settlements`, { params: filters });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching settlements:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch settlements',
        data: []
      };
    }
  }
}

/**
 * Get all vendors with limited fields (label, name, vendorId, _id)
 * This is a static method that doesn't require a specific gateway type
 */
export const getAllVendorsLimited = async (): Promise<{
  success: boolean;
  count?: number;
  data: Array<{ _id: string; label: string; name: string; vendorId: string }>;
  message?: string;
}> => {
  try {
    const response = await apiService.get('/payment-gateway/all-vendors/limited');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching limited vendors:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch vendors',
      data: []
    };
  }
};

// Export instances for each gateway type
export const cashfreeGatewayService = new PaymentGatewayService('cashfree');
export const manualGatewayService = new PaymentGatewayService('manual');

// Export class for custom instantiation
export { PaymentGatewayService };

// Default export (cashfree for backward compatibility)
export default cashfreeGatewayService;
